import {
  WindowTransport,
  createCantonClient,
  type CantonClient,
} from "@sigilry/dapp";
import type {
  CantonAccount,
  CantonConnectResult,
  CantonConnector,
  CantonConnectorEvent,
  CantonConnectorEventHandler,
  CantonConnectorEventMap,
  CantonLedgerApiParams,
  CantonPrepareExecuteAndWaitResult,
  CantonPrepareExecuteParams,
} from "../types/canton";
import { AnnouncedCantonProvider } from "./cip103Client";

const REQUEST_TIMEOUT_MS = 60_000;

function pickAddress(accounts: CantonAccount[]): string | undefined {
  return (accounts.find((a) => a.primary) ?? accounts[0])?.partyId;
}

/**
 * Browser-extension Canton connector: wraps one discovered CIP-103 provider
 * (bound to its announce `target`) and exposes the lifecycle methods.
 */
export class CantonBrowserConnector implements CantonConnector {
  readonly providerId: string;

  readonly name: string;

  readonly target: string | undefined;

  private readonly client: CantonClient;

  private readonly listeners: {
    [E in CantonConnectorEvent]: Set<CantonConnectorEventHandler<E>>;
  } = { connect: new Set(), disconnect: new Set() };

  private _accounts: CantonAccount[] = [];

  constructor(
    provider: AnnouncedCantonProvider,
    win: Window = window,
    client?: CantonClient
  ) {
    this.providerId = provider.id;
    this.name = provider.name;
    this.target = provider.target ?? provider.id;
    this.client =
      client ??
      createCantonClient(
        new WindowTransport(win, {
          target: this.target,
          timeout: REQUEST_TIMEOUT_MS,
        })
      );
  }

  get accounts(): CantonAccount[] {
    return this._accounts;
  }

  get address(): string | undefined {
    return pickAddress(this._accounts);
  }

  async connect(): Promise<CantonConnectResult> {
    await this.client.connect();
    const result = await this.refreshAccounts();
    this.emit("connect", result.address);
    return result;
  }

  /** Silent connect: read accounts without prompting; throws if not connected. */
  async autoConnect(): Promise<CantonConnectResult> {
    const accounts = await this.client.listAccounts();
    if (!accounts.length) {
      throw new Error("Canton wallet not connected");
    }
    this._accounts = accounts;
    const address = pickAddress(accounts)!;
    this.emit("connect", address);
    return { address, accounts };
  }

  async disconnect(): Promise<void> {
    try {
      await this.client.disconnect();
    } finally {
      this._accounts = [];
      this.emit("disconnect");
    }
  }

  async ledgerApi<T = unknown>(params: CantonLedgerApiParams): Promise<T> {
    const result = await this.client.ledgerApi(params);
    return result as unknown as T;
  }

  async prepareExecuteAndWait(
    params: CantonPrepareExecuteParams
  ): Promise<CantonPrepareExecuteAndWaitResult> {
    return this.client.prepareExecuteAndWait(params);
  }

  async getAccounts(): Promise<CantonAccount[]> {
    this._accounts = await this.client.listAccounts();
    return this._accounts;
  }

  on<E extends CantonConnectorEvent>(
    event: E,
    handler: CantonConnectorEventHandler<E>
  ): void {
    this.listeners[event].add(handler);
  }

  off<E extends CantonConnectorEvent>(
    event: E,
    handler: CantonConnectorEventHandler<E>
  ): void {
    this.listeners[event].delete(handler);
  }

  private emit<E extends CantonConnectorEvent>(
    event: E,
    ...args: CantonConnectorEventMap[E]
  ): void {
    this.listeners[event].forEach((handler) => handler(...args));
  }

  private async refreshAccounts(): Promise<CantonConnectResult> {
    const accounts = await this.client.listAccounts();
    this._accounts = accounts;
    const address = pickAddress(accounts);
    if (!address) {
      throw new Error("Canton wallet returned no accounts");
    }
    return { address, accounts };
  }
}
